/**
 * Consolidate a process view's work items into groups, and tag them with the
 * group they belong to.
 *
 * A process that walks items one at a time sometimes wants to act on a whole
 * batch instead: every item bound for the same destination, every operation at
 * the same station. That is one shape — group by a field, order by a sequence
 * the record already carries, and report progress per group — and none of it is
 * about warehouses once the field names come from config.
 *
 * The sequence and tag both read a list on the record (a solved route, a
 * planned order of work) and index it by one of its own fields.
 */

const matches = (row, rule) =>
    !rule || row?.[rule.field] === rule.value

const excluded = (row, rule) =>
    !!rule && row?.[rule.field] === rule.value

/** First of `fields` that the item actually has a value for. */
const firstValue = (item, fields) => {
    for (const f of [].concat(fields ?? [])) {
        const v = item?.[f]
        if (v !== undefined && v !== null) return v
    }
    return 0
}

/** locationId → warehouseCode, or whatever the config names them. */
export function indexBy(record, spec) {
    const out = new Map()
    if (!spec?.from || !spec?.key) return out
    for (const row of record?.[spec.from] ?? []) {
        if (excluded(row, spec.exclude)) continue
        if (spec.include && !matches(row, spec.include)) continue
        const key = row[spec.key]
        if (key == null) continue
        const value = spec.value ? row[spec.value] : row
        if (value != null) out.set(key, value)
    }
    return out
}

/**
 * The group label for one item: the first of `matchFields` that the indexed
 * list knows about. An item bound for an unlisted place has no tag, which is
 * what leaves a single-destination process ungrouped.
 */
export function tagFor(item, spec, index) {
    if (!spec?.matchFields) return null
    for (const field of spec.matchFields) {
        const found = index.get(item?.[field])
        if (found != null) return found
    }
    return null
}

/**
 * @returns one entry per distinct value of `spec.by`, ordered by the record's
 *          sequence where it has one and left at the end where it does not.
 */
export function groupItems(items, spec, record) {
    if (!spec?.by) return []

    const byKey = new Map()
    for (const item of items ?? []) {
        if (spec.include && !matches(item, spec.include)) continue
        const key = item[spec.by]
        if (key == null) continue
        if (!byKey.has(key)) byKey.set(key, [])
        byKey.get(key).push(item)
    }

    const order = indexBy(record, spec.sequence && { ...spec.sequence, value: spec.sequence.order })
    const tags  = indexBy(record, spec.tag)

    const summarize = (group) => {
        const totals = new Map()
        for (const item of group) {
            const name = item[spec.labelField] || item[spec.by] || 'item'
            totals.set(name, (totals.get(name) || 0) + firstValue(item, spec.quantity))
        }
        return [...totals.entries()].map(([name, qty]) => `${name} ×${qty}`).join(', ')
    }

    const groups = [...byKey.entries()].map(([key, group]) => ({
        id:       key,
        [spec.by]: key,
        seq:      order.get(key) ?? 999,
        quantity: group.reduce((sum, item) => sum + firstValue(item, spec.quantity), 0),
        summary:  summarize(group),
        allDone:  group.every(item => item[spec.doneField]),
        itemIds:  group.map(item => item.id),
        _tag:     tags.get(key) ?? null,
    }))

    groups.sort((a, b) => a.seq - b.seq)
    return groups
}
